import { Component, forwardRef, Injector, Input, OnDestroy } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { filter, Subscription } from 'rxjs';

import { DateQueryModel } from '../../models/date-query.model';
import { DateInputTypesEnum } from '../../enums/date-input-types.enum';
import { DateQueryTypeEnum } from '../../../../api/mentions/enum/date-query-type.enum';
import { DropdownOptionModel } from '../../models/dropdown-option.model';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { DateQueryInputDialogComponent } from '../../../Dialogs/components/DateQueryInputDialog/date-query-input-dialog.component';
import { DateQueryInputDialogModel } from '../../../Dialogs/models/date-query-input-dialog.model';
import { SearchRangeModel } from '../../../../Monitor/Core/models/search-range.model';
import { PreferencesModel } from '../../../../Core/Users/models/preferences.model';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { DateHelper } from '../../helpers/date.helper';
import { getDefaultDateInputOptionValuesModel } from '../../models/date-query-input-options.model';
import { AgprComponentStyleEnum } from '../../../Basic/enums/social-analysis-types.enum';
import { defaultDateTypeOptions } from '../DateRangeSelector/date-range-selector.component';
import { CachedDateService } from '../../../MonitorShared/services/cached-date.service';

/**
 * ### Accessibility Usage
 * + ***ariaLabelledBy*** - use this to associate an external label with the component. Use id of the label as this value
 * + ***id*** - provide an unique id so that this component can be correctly recognized by screen readers. Use interpolation [id]="'unique_id'"
 * + ***tooltipLabel*** - add tooltip to the dropdown button
 */
@Component({
    selector: 'agpr-date-query-popup',
    templateUrl: './date-query-popup.component.html',
    styleUrls: ['./date-query-popup.component.scss'],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => DateQueryPopupComponent),
        multi: true,
    }],
    standalone: false
})
export class DateQueryPopupComponent implements ControlValueAccessor, OnDestroy {

    @Input() id: string = null;
    @Input() disabled: boolean = false;
    @Input() header: string = 'Monitoring.dateQueryDialog.header';
    @Input() searchRange: SearchRangeModel;
    @Input() useToDate: boolean = false;
    @Input() setEndOfDayTime: boolean = true;
    @Input() dateTypeOptions: DropdownOptionModel[] = defaultDateTypeOptions;
    @Input() toolTipLabel: string = 'Monitoring.bulkActionDialog.dateRange';
    @Input() style: AgprComponentStyleEnum = AgprComponentStyleEnum.default;
    @Input() hideDateTypeSelection: boolean = false;
    @Input() inputPlaceholder: string = '';
    @Input() ariaLabelledBy: string;
    @Input() useDateSearchRangeSelection = true;
    @Input() dropdownInfoTooltip: string | null = null;

    @Input()
    set options(options: DropdownOptionModel[]) {
        if (options?.length > 0) {
            this._options = options;
            this.updateDropdownValue();
        } else {
            this._options = getDefaultDateInputOptionValuesModel();
        }
    }
    get options(): DropdownOptionModel[] {
        return this._options;
    }

    _options: DropdownOptionModel[] = [];

    _onChange: Function = this.defaultOnChange;
    _onTouched: Function;

    dropdownValue: DropdownOptionModel = null;
    initialValue: DropdownOptionModel;
    customOptionSelected: boolean = false;

    private value: DateQueryModel;

    private subscriptions: Subscription[] = [];
    private preferences: PreferencesModel;

    readonly agprComponentStyle = AgprComponentStyleEnum;

    constructor(
        protected injector: Injector,
        protected coreDialogService: CoreDialogService,
        public currentUserStateService: CurrentUserStateService,
        public cachedDateService: CachedDateService
    ) {
        this.subscriptions.push(
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(
                preferences => {
                    this.preferences = preferences;
                    if (this.value) {
                        this.updateTimezone();
                    }
                }
            )
        );
    }


    open(): void {
        if (this.disabled) {
            return;
        }
        const io: DateQueryInputDialogModel = {
            inputs: {
                id: this.id,
                header: this.header,
                options: this._options,
                selectedOption: this.customOptionSelected ? this._options.find(x => x.value.type === DateInputTypesEnum.custom) : this.dropdownValue,
                dateTypeOptions: this.dateTypeOptions,
                dateRange: this.searchRange,
                useToDate: this.useToDate,
                setEndOfDayTime: this.setEndOfDayTime,
                hideDateTypeSelection: this.hideDateTypeSelection,
                inputPlaceholder: this.inputPlaceholder
            },
            outputs: {
                dialogClose: (selectedOption) => {
                    this.applyChanges(selectedOption);
                }
            }
        };
        this.coreDialogService.open(DateQueryInputDialogComponent, io, this.injector);
    }

    /**
     * Event triggered by dropdown after a selection happens
     */
    public applyChanges(selectedOption): void {
        // A null event means the user cancelled out of the dialog so we don't need to do anything.
        if (!selectedOption) {
            // If initial value exists its because the dialog was opened by selecting the custom dropdown option.
            // In this case we need to revert back to the original selected option, otherwise the custom option will remain selected in the dropdown.
            if (this.initialValue) {
                this.dropdownValue = this.initialValue;
                this.initialValue = null;
            }
            return;
        }

        this.value = selectedOption.value.query;
        this.cachedDateService.setCachedSelectedDate(this.value);
        this.updateTimezone();
        this.updateDropdownValue();
        this._onChange(this.value);
        this._onTouched();
    }

    /**
     * This is a separate, public function so that it can be tested
     */
    public defaultOnChange() {

    }

    /**
     * Called when something other than the dropdown sets the value, to update this.dropdownValue to reflect the new value
     */
    private updateDropdownValue(): void {
        if (!this.value) {
            return;
        }

        const dropdownOption = this._options.find((option) => {
            if (option && option.value && option.value.query) {
                return DateQueryModel.equals(option.value.query, this.value, false);
            } else {
                return false;
            }
        });

        if (dropdownOption) {
            dropdownOption.value.query.dateType = this.value.dateType;
            if (dropdownOption.value.type === DateInputTypesEnum.custom) {
                this.customOptionSelected = true;
                this.dropdownValue = this.createNewCustomOption();
            } else { // Preset option
                this.customOptionSelected = false;
                this.dropdownValue = dropdownOption;
            }
        } else {
            // No match from list of options, could not exist, could be 'custom'
            const customOption = this._options.find((option) => option?.value?.type === DateInputTypesEnum.custom);
            if (customOption !== null &&
                customOption !== undefined &&
                customOption.value.query.type === this.value.type
            ) {
                this._options = this._options.map(option => {
                    if (option.value.type !== DateInputTypesEnum.custom) {
                        return option;
                    }

                    return this.getUpdatedCustomDateOption(option);
                });
                // If there's a custom option, none of the other options matched and the type of this matches the type of the custom option, choose custom
                this.customOptionSelected = true;
                this.dropdownValue = this.createNewCustomOption();
            } else {
                this.customOptionSelected = false;
                this.dropdownValue = null;
            }
        }
    }

    // create a new custom option which is used by the dropdown to display the custom date range
    private createNewCustomOption(): DropdownOptionModel {
        const option = new DropdownOptionModel();
        option.label = this.getDisplayValue();
        option.value = {
            query: this.value,
            type: DateInputTypesEnum.custom
        };

        return option;
    }

    /**
     * Returns DropdownOptionModel with dates reflecting current value
     */
    private getUpdatedCustomDateOption(customDateOption: DropdownOptionModel): DropdownOptionModel {
        const customDropdownOption =  new DropdownOptionModel(
            customDateOption.label,
            {
                query: DateQueryModel.getAbsoluteQuery(),
                type: DateInputTypesEnum.custom
            }
        );

        if (this.value && this.value.type === DateQueryTypeEnum.ABSOLUTE) {
            if (this.value.absoluteDate) {
                (<DateQueryModel>customDropdownOption.value.query).absoluteDate = this.value.absoluteDate;
            } else {
                (<DateQueryModel>customDropdownOption.value.query).absoluteRange = this.value.absoluteRange;
            }

            (<DateQueryModel>customDropdownOption.value.query).dateType = this.value.dateType;
        }

        return customDropdownOption;
    }

    /**
     * Returns a date string for custom option
     */
    private getDisplayValue(): string {
        // Value must be absolute, set absolute display value
        if (this.value.absoluteDate) {
            return DateHelper.toDateString(this.value.absoluteDate);
        } else {
            if (this.value.absoluteRange) {
                return DateHelper.toDateString(this.value.absoluteRange.start, this.preferences?.timeZone)
                    + ' - ' + DateHelper.toDateString(this.value.absoluteRange.end, this.preferences?.timeZone);
            }
            return '';
        }
    }

    /**
     * ControlValueAccessor
     */
    public writeValue(value: DateQueryModel) {
        this.value = value;

        this.updateDropdownValue();
    }

    public registerOnChange(onChange: Function) {
        this._onChange = onChange;
    }

    public registerOnTouched(onTouched: Function) {
        this._onTouched = onTouched;
    }

    public dropdownSelectionChanged(selectedOption: DropdownOptionModel) {
        requestAnimationFrame(() => {
            if (selectedOption.value.type === DateInputTypesEnum.custom) {
                // Save the current selected option so if the dialog is cancelled we can revert back.
                this.initialValue = this.dropdownValue;
                this.dropdownValue = selectedOption;
                this.open();
            } else {
                this.initialValue = null;
                this.dropdownValue = selectedOption;
                this.applyChanges(this.dropdownValue);
            }
        });
    }

    private updateTimezone(): void {
        if (!this.value) {
            return;
        }
        if (this.preferences?.timeZone) {
            if (this.value.type === DateQueryTypeEnum.RELATIVE_TIMEZONE || this.value.type === DateQueryTypeEnum.ABSOLUTE) {
                this.value.timezone = this.preferences.timeZone;
            } else {
                this.value.timezone = null;
            }
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
